import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Slider,
  InputAdornment,
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface Lead {
  id: string;
  name: string;
  phone: string;
  email?: string;
  source: string;
  stage: string;
  pipeline: string;
  lastContact?: string;
}

// Interface Agent removida - não mais necessária

interface Prompt {
  id: string;
  name: string;
  description: string;
  content: string;
  category: string;
  tags: string[];
}

interface Campaign {
  id?: string;
  name: string;
  description: string;
  leads: Lead[];
  agent: any | null; // Mantido para compatibilidade, mas não será usado
  prompt: Prompt | null;
  settings: {
    maxCallsPerHour: number;
    retryAttempts: number;
    callTimeout: number;
    waitBetweenCalls: number;
    enableVoicemail: boolean;
    enableSMS: boolean;
  };
  status: 'draft' | 'active' | 'paused' | 'completed';
}

const VoiceCreate: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [campaign, setCampaign] = useState<Campaign>({
    name: '',
    description: '',
    leads: [],
    agent: null, // Mantido para compatibilidade com a interface, mas não será usado
    prompt: null,
    settings: {
      maxCallsPerHour: 10,
      retryAttempts: 3,
      callTimeout: 30,
      waitBetweenCalls: 5,
      enableVoicemail: true,
      enableSMS: false,
    },
    status: 'draft',
  });

  const [availableLeads, setAvailableLeads] = useState<Lead[]>([]);
  const [availablePrompts, setAvailablePrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState<string>('');
  
  // Estados para criação de script
  const [newScript, setNewScript] = useState({
    name: '',
    description: '',
    content: '',
    category: 'Vendas',
    tags: [] as string[]
  });
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState<string>('');
  
  // Filtros para leads
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [pipelineFilter, setPipelineFilter] = useState<string>('all');
  
  // API URL
  const API_BASE_URL = getApiUrl();

      // Função para buscar leads reais da API
    const fetchLeads = async () => {
      try {
        setLeadsLoading(true);
        setLeadsError('');
        
        const response = await axios.get(`${API_BASE_URL}/crm/all-leads`);
        
        if (!response.data.success) {
          throw new Error('Falha ao buscar leads');
        }
        
        // Transformar dados da API para o formato esperado
        const leads = response.data.data.map((lead: any) => ({
          id: lead.contacts?.id || lead.id,
          name: lead.contacts?.name || 'Nome não informado',
          phone: lead.contacts?.phone || '',
          email: lead.contacts?.email || '',
          source: lead.contacts?.source || 'webchat',
          stage: lead.crm_stages?.name || 'Novo Lead',
          pipeline: lead.crm_pipelines?.name || 'Funil de Vendas',
          lastContact: lead.entered_at || new Date().toISOString(),
        }));
        
        setAvailableLeads(leads);
        console.log('Leads carregados da API:', leads.length);
        
      } catch (error) {
        console.error('Erro ao buscar leads:', error);
        setLeadsError('Erro ao carregar leads. Tente novamente.');
        
        // Em caso de erro, manter array vazio
        setAvailableLeads([]);
      } finally {
        setLeadsLoading(false);
      }
    };

    // Carregar leads ao montar o componente
    useEffect(() => {
      fetchLeads();
    }, []);

  // Carregar prompts da API
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/voice/prompts`);
        if (response.data.success && response.data.data) {
          setAvailablePrompts(response.data.data);
        } else {
          // Se não houver dados, manter array vazio
          setAvailablePrompts([]);
        }
      } catch (error) {
        console.log('Erro ao buscar prompts da API');
        // Em caso de erro, manter array vazio
        setAvailablePrompts([]);
      }
    };
    fetchPrompts();
  }, []);

  const steps = [
    'Informações Básicas',
    'Seleção de Leads',
    'Script da Ligação',
    'Configurações Avançadas',
    'Revisão e Ativação',
  ];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleLeadToggle = (lead: Lead) => {
    setCampaign(prev => ({
      ...prev,
      leads: prev.leads.find(l => l.id === lead.id)
        ? prev.leads.filter(l => l.id !== lead.id)
        : [...prev.leads, lead],
    }));
  };

  const handleSelectAllLeads = () => {
    const filteredLeads = getFilteredLeads();
    setCampaign(prev => ({
      ...prev,
      leads: filteredLeads,
    }));
  };

  const handleDeselectAllLeads = () => {
    setCampaign(prev => ({
      ...prev,
      leads: [],
    }));
  };

  const isAllSelected = () => {
    const filteredLeads = getFilteredLeads();
    return filteredLeads.length > 0 && filteredLeads.every(lead => 
      campaign.leads.find(l => l.id === lead.id)
    );
  };

  const getSelectedCount = () => {
    const filteredLeads = getFilteredLeads();
    return filteredLeads.filter(lead => 
      campaign.leads.find(l => l.id === lead.id)
    ).length;
  };

  const handleSaveCampaign = async () => {
    setLoading(true);
    try {
      // Simular salvamento
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Campanha salva:', campaign);
      navigate('/campaigns/voice');
    } catch (error) {
      console.error('Erro ao salvar campanha:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateCampaign = async () => {
    setLoading(true);
    try {
      // Simular ativação
      await new Promise(resolve => setTimeout(resolve, 3000));
      console.log('Campanha ativada:', campaign);
      navigate('/campaigns/voice');
    } catch (error) {
      console.error('Erro ao ativar campanha:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScript = async () => {
    if (!newScript.name.trim() || !newScript.content.trim()) {
      setScriptError('Nome e conteúdo do script são obrigatórios');
      return;
    }

    setScriptLoading(true);
    setScriptError('');

    try {
      // Simular salvamento do script
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const savedScript: Prompt = {
        id: `script-${Date.now()}`, // ID único
        name: newScript.name.trim(),
        description: newScript.description.trim(),
        content: newScript.content.trim(),
        category: newScript.category,
        tags: newScript.tags
      };

      // Adicionar o novo script à lista
      setAvailablePrompts(prev => [...prev, savedScript]);
      
      // Selecionar automaticamente o novo script
      setCampaign(prev => ({ ...prev, prompt: savedScript }));
      
      // Limpar formulário
      setNewScript({
        name: '',
        description: '',
        content: '',
        category: 'Vendas',
        tags: []
      });
      
      // Fechar dialog
      setShowPreview(false);
      
      console.log('Script salvo:', savedScript);
    } catch (error) {
      console.error('Erro ao salvar script:', error);
      setScriptError('Erro ao salvar script. Tente novamente.');
    } finally {
      setScriptLoading(false);
    }
  };

  const handleAddTag = (tag: string) => {
    if (tag.trim() && !newScript.tags.includes(tag.trim())) {
      setNewScript(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setNewScript(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Função para filtrar leads
  const getFilteredLeads = () => {
    const filtered = availableLeads.filter(lead => {
      // Filtrar apenas leads com telefone
      if (!lead.phone || lead.phone.trim() === '') {
        return false;
      }
      
      // Filtrar por canal
      if (channelFilter !== 'all' && lead.source !== channelFilter) {
        return false;
      }
      
      // Filtrar por estágio
      if (stageFilter !== 'all' && lead.stage !== stageFilter) {
        return false;
      }
      
      // Filtrar por pipeline
      if (pipelineFilter !== 'all' && lead.pipeline !== pipelineFilter) {
        return false;
      }
      
      return true;
    });
    
    console.log('getFilteredLeads:', {
      totalLeads: availableLeads.length,
      filteredCount: filtered.length,
      channelFilter,
      stageFilter,
      pipelineFilter
    });
    
    return filtered;
  };

  // Obter opções únicas para filtros
  const getUniqueStages = () => {
    const stages = availableLeads
      .filter(lead => lead.phone && lead.phone.trim() !== '')
      .map(lead => lead.stage);
    return Array.from(new Set(stages)).sort();
  };

  const getUniquePipelines = () => {
    const pipelines = availableLeads
      .filter(lead => lead.phone && lead.phone.trim() !== '')
      .map(lead => lead.pipeline);
    return Array.from(new Set(pipelines)).sort();
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return theme.palette.success.main;
      case 'telegram': return theme.palette.info.main;
      case 'instagram': return theme.palette.secondary.main;
      case 'webchat': return theme.palette.warning.main;
      default: return theme.palette.grey[500];
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações Básicas da Campanha
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <TextField
                    fullWidth
                    label="Nome da Campanha"
                    value={campaign.name}
                    onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Campanha de Vendas Q4"
                    required
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <TextField
                    fullWidth
                    label="Descrição"
                    value={campaign.description}
                    onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva o objetivo da campanha"
                    multiline
                    rows={3}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <>
            {/* Barra de Navegação Superior */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              p: 2,
              bgcolor: theme.palette.background.paper,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Seleção de Leads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getFilteredLeads().length} leads disponíveis • {campaign.leads.length} selecionados
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  leadsLoading || 
                  campaign.leads.length === 0
                }
                sx={{ 
                  bgcolor: theme.palette.warning.main, 
                  '&:hover': { bgcolor: theme.palette.warning.dark },
                  minWidth: 120
                }}
              >
                Próximo
              </Button>
            </Box>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Seleção de Leads
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Selecione os leads que receberão as ligações. Apenas leads com telefone configurado são exibidos.
                </Typography>
              
              {/* Filtros */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Filtros
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Canal</InputLabel>
                    <Select
                      value={channelFilter}
                      onChange={(e) => setChannelFilter(e.target.value)}
                      label="Canal"
                    >
                      <MenuItem value="all">Todos os Canais</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="telegram">Telegram</MenuItem>
                      <MenuItem value="instagram">Instagram</MenuItem>
                      <MenuItem value="webchat">Webchat</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Estágio do CRM</InputLabel>
                    <Select
                      value={stageFilter}
                      onChange={(e) => setStageFilter(e.target.value)}
                      label="Estágio do CRM"
                    >
                      <MenuItem value="all">Todos os Estágios</MenuItem>
                      {getUniqueStages().map((stage) => (
                        <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Pipeline</InputLabel>
                    <Select
                      value={pipelineFilter}
                      onChange={(e) => setPipelineFilter(e.target.value)}
                      label="Pipeline"
                    >
                      <MenuItem value="all">Todos os Pipelines</MenuItem>
                      {getUniquePipelines().map((pipeline) => (
                        <MenuItem key={pipeline} value={pipeline}>{pipeline}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Loading e Erro */}
              {leadsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {leadsError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={fetchLeads}
                      disabled={leadsLoading}
                    >
                      Tentar Novamente
                    </Button>
                  }
                >
                  <Typography variant="body2">
                    {leadsError}
                  </Typography>
                </Alert>
              )}

              {/* Botões de Seleção */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSelectAllLeads}
                  disabled={leadsLoading || getFilteredLeads().length === 0}
                  startIcon={<CheckCircleIcon />}
                >
                  Selecionar Todos ({getFilteredLeads().length})
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDeselectAllLeads}
                  disabled={leadsLoading || campaign.leads.length === 0}
                  startIcon={<RemoveIcon />}
                >
                  Desmarcar Todos
                </Button>
                <Chip
                  label={`${getSelectedCount()} de ${getFilteredLeads().length} selecionados`}
                  color={isAllSelected() ? "success" : "default"}
                  size="small"
                  icon={isAllSelected() ? <CheckCircleIcon /> : undefined}
                />
              </Box>

              {/* Estatísticas */}
              <Box sx={{ mb: 2, p: 2, bgcolor: theme.palette.background.default, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{getFilteredLeads().length}</strong> leads disponíveis com telefone • 
                  <strong> {campaign.leads.length}</strong> selecionados
                  {leadsLoading && ' • Carregando...'}
                </Typography>
              </Box>

              <List>
                {getFilteredLeads().map((lead) => (
                  <React.Fragment key={lead.id}>
                    <ListItem>
                      <ListItemText
                        primary={lead.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {lead.phone} • {lead.email}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={lead.source}
                                size="small"
                                sx={{ 
                                  mr: 1, 
                                  bgcolor: getChannelColor(lead.source),
                                  color: '#000000',
                                  fontWeight: 'medium'
                                }}
                              />
                              <Chip
                                label={lead.stage}
                                size="small"
                                sx={{ 
                                  mr: 1, 
                                  bgcolor: theme.palette.primary.light,
                                  color: '#000000',
                                  fontWeight: 'medium'
                                }}
                              />
                              <Chip
                                label={lead.pipeline}
                                size="small"
                                sx={{ 
                                  bgcolor: theme.palette.secondary.light,
                                  color: '#000000',
                                  fontWeight: 'medium'
                                }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleLeadToggle(lead)}
                          color={campaign.leads.find(l => l.id === lead.id) ? 'primary' : 'default'}
                        >
                          {campaign.leads.find(l => l.id === lead.id) ? <CheckCircleIcon /> : <AddIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              {getFilteredLeads().length === 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Nenhum lead encontrado</strong> com os filtros aplicados. 
                    Tente ajustar os filtros ou verifique se há leads com telefone configurado.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>{campaign.leads.length}</strong> leads selecionados para esta campanha.
                    {campaign.leads.length > 0 && (
                      <span> • Total de {getFilteredLeads().length} leads disponíveis com telefone.</span>
                    )}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
        );

      case 2:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Script da Ligação
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Escolha um prompt pré-definido ou crie um script personalizado para as ligações.
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Selecionar Prompt</InputLabel>
                <Select
                  value={campaign.prompt?.id || ''}
                  onChange={(e) => {
                    const prompt = availablePrompts.find(p => p.id === e.target.value);
                    setCampaign(prev => ({ ...prev, prompt: prompt || null }));
                  }}
                  label="Selecionar Prompt"
                >
                  {availablePrompts.map((prompt) => (
                    <MenuItem key={prompt.id} value={prompt.id}>
                      <Box>
                        <Typography variant="body1">{prompt.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {prompt.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

                            {campaign.prompt && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {campaign.prompt.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {campaign.prompt.description}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={campaign.prompt.category}
                            size="small"
                            sx={{ mr: 1, bgcolor: theme.palette.primary.light, color: '#000000' }}
                          />
                          {campaign.prompt.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              sx={{ mr: 1, bgcolor: theme.palette.secondary.light, color: '#000000' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    
                    <Typography variant="body1" sx={{ 
                      bgcolor: theme.palette.background.default, 
                      p: 2, 
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {campaign.prompt.content}
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Variáveis disponíveis: {'{agente}, {nome}, {empresa}, {benefício}'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => setShowPreview(true)}
              >
                Criar Script Personalizado
              </Button>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configurações Avançadas
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Configure parâmetros avançados para otimizar a campanha.
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Limites de Chamadas
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Máximo de chamadas por hora"
                    value={campaign.settings.maxCallsPerHour}
                    onChange={(e) => setCampaign(prev => ({
                      ...prev,
                      settings: { ...prev.settings, maxCallsPerHour: parseInt(e.target.value) }
                    }))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">chamadas/h</InputAdornment>,
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Tentativas de Retry
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Número de tentativas"
                    value={campaign.settings.retryAttempts}
                    onChange={(e) => setCampaign(prev => ({
                      ...prev,
                      settings: { ...prev.settings, retryAttempts: parseInt(e.target.value) }
                    }))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">tentativas</InputAdornment>,
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Timeout da Chamada
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tempo máximo de chamada"
                    value={campaign.settings.callTimeout}
                    onChange={(e) => setCampaign(prev => ({
                      ...prev,
                      settings: { ...prev.settings, callTimeout: parseInt(e.target.value) }
                    }))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">minutos</InputAdornment>,
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Intervalo entre Chamadas
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tempo de espera"
                    value={campaign.settings.waitBetweenCalls}
                    onChange={(e) => setCampaign(prev => ({
                      ...prev,
                      settings: { ...prev.settings, waitBetweenCalls: parseInt(e.target.value) }
                    }))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">minutos</InputAdornment>,
                    }}
                  />
                </Box>

                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Opções Adicionais
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaign.settings.enableVoicemail}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, enableVoicemail: e.target.checked }
                        }))}
                      />
                    }
                    label="Deixar mensagem na caixa postal"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaign.settings.enableSMS}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, enableSMS: e.target.checked }
                        }))}
                      />
                    }
                    label="Enviar SMS de follow-up"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        );

      case 4:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revisão e Ativação
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Revise todas as configurações antes de ativar a campanha.
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Informações da Campanha
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <Typography variant="body2">
                      <strong>Nome:</strong> {campaign.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Descrição:</strong> {campaign.description}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Leads:</strong> {campaign.leads.length} selecionados
                    </Typography>
                    <Typography variant="body2">
                      <strong>Agente:</strong> {campaign.agent?.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Script:</strong> {campaign.prompt?.name}
                    </Typography>
                  </Paper>
                </Box>

                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Configurações
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <Typography variant="body2">
                      <strong>Chamadas/hora:</strong> {campaign.settings.maxCallsPerHour}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tentativas:</strong> {campaign.settings.retryAttempts}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Timeout:</strong> {campaign.settings.callTimeout} min
                    </Typography>
                    <Typography variant="body2">
                      <strong>Intervalo:</strong> {campaign.settings.waitBetweenCalls} min
                    </Typography>
                    <Typography variant="body2">
                      <strong>Caixa postal:</strong> {campaign.settings.enableVoicemail ? 'Sim' : 'Não'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>SMS follow-up:</strong> {campaign.settings.enableSMS ? 'Sim' : 'Não'}
                    </Typography>
                  </Paper>
                </Box>
              </Box>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Atenção:</strong> Após ativar a campanha, as ligações serão iniciadas automaticamente 
                  de acordo com as configurações definidas. Certifique-se de que todas as informações estão corretas.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/campaigns/voice')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Criar Campanha de Ligação
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      {renderStepContent(activeStep)}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Voltar
        </Button>

        <Box>
          {activeStep === steps.length - 1 ? (
            <>
              <Button
                variant="outlined"
                onClick={handleSaveCampaign}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ mr: 2 }}
              >
                Salvar Rascunho
              </Button>
              <Button
                variant="contained"
                onClick={handleActivateCampaign}
                disabled={loading || !campaign.name || !campaign.prompt || campaign.leads.length === 0}
                startIcon={loading ? <CircularProgress size={20} /> : <PlayIcon />}
                sx={{ bgcolor: theme.palette.warning.main, '&:hover': { bgcolor: theme.palette.warning.dark } }}
              >
                Ativar Campanha
              </Button>
            </>
          ) : (
            <>
              {activeStep !== 1 && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                                disabled={
                (activeStep === 0 && !campaign.name) ||
                (activeStep === 2 && !campaign.prompt)
              }
                  sx={{ bgcolor: theme.palette.warning.main, '&:hover': { bgcolor: theme.palette.warning.dark } }}
                >
                  Próximo
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Custom Script Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Criar Script Personalizado</DialogTitle>
        <DialogContent>
          {scriptError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {scriptError}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Nome do Script *"
            placeholder="Ex: Vendas Personalizadas"
            value={newScript.name}
            onChange={(e) => setNewScript(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
            required
          />
          
          <TextField
            fullWidth
            label="Descrição"
            placeholder="Descreva o objetivo deste script"
            value={newScript.description}
            onChange={(e) => setNewScript(prev => ({ ...prev, description: e.target.value }))}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={newScript.category}
              onChange={(e) => setNewScript(prev => ({ ...prev, category: e.target.value }))}
              label="Categoria"
            >
              <MenuItem value="Vendas">Vendas</MenuItem>
              <MenuItem value="Suporte">Suporte</MenuItem>
              <MenuItem value="Follow-up">Follow-up</MenuItem>
              <MenuItem value="Qualificação">Qualificação</MenuItem>
              <MenuItem value="Outros">Outros</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Tags (separadas por vírgula)"
            placeholder="Ex: B2B, Empresarial, Soluções"
            value={newScript.tags.join(', ')}
            onChange={(e) => {
              const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
              setNewScript(prev => ({ ...prev, tags }));
            }}
            sx={{ mb: 2 }}
            helperText="Digite tags separadas por vírgula para categorizar o script"
          />
          
          <TextField
            fullWidth
            label="Conteúdo do Script *"
            placeholder="Digite o script da ligação..."
            value={newScript.content}
            onChange={(e) => setNewScript(prev => ({ ...prev, content: e.target.value }))}
            multiline
            rows={8}
            required
            helperText="Use variáveis como {nome}, {agente}, {empresa} para personalização"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowPreview(false);
              setNewScript({
                name: '',
                description: '',
                content: '',
                category: 'Vendas',
                tags: []
              });
              setScriptError('');
            }}
            disabled={scriptLoading}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveScript}
            disabled={scriptLoading || !newScript.name.trim() || !newScript.content.trim()}
            startIcon={scriptLoading ? <CircularProgress size={20} /> : undefined}
          >
            {scriptLoading ? 'Salvando...' : 'Salvar Script'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceCreate; 